import React, { useState, useEffect } from 'react';
import { Tab, Tabs, Form, Button, Spinner, Table, Badge, Alert } from 'react-bootstrap';
import Select from 'react-select';
import api from '../../../../services/api';

interface ParameterItem {
  name: string;
  type: string;
  default_value: string;
}

interface TestParameter {
  name: string;
  type: string;
  value: string;
}

interface TestResult {
  success: boolean;
  http_status: number | null;
  headers: Record<string, string>;
  body: any;
  output_parameters: {
    name: string;
    mapping: string;
    value: any;
    error: string | null;
  }[];
  error?: string;
}

interface Connection {
  connection_id: number;
  connection_name: string;
}

interface Props {
  nodeId: number;
  integrationId: number;
  parameters: ParameterItem[];
  inheritAuth: boolean;
  authBasic: boolean;
  authApiToken: boolean;
  authOAuth2: boolean;
  unsaved: boolean;
}

const TESTABLE_TYPES = ['String', 'Number'];

function prettyPrintXml(xml: string): string {
  let formatted = '';
  let indent = 0;
  const parts = xml.replace(/>\s*</g, '><').split(/(?<=>)(?=<)/);
  for (const part of parts) {
    if (part.startsWith('</')) indent -= 2;
    formatted += ' '.repeat(Math.max(0, indent)) + part + '\n';
    if (!part.startsWith('</') && !part.endsWith('/>') && part.includes('<') && !part.includes('</')) {
      indent += 2;
    }
  }
  return formatted.trim();
}

function formatBody(body: any): string {
  if (body === null || body === undefined) return '';
  if (typeof body === 'object') {
    return JSON.stringify(body, null, 2);
  }
  const str = String(body);
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    const trimmed = str.trimStart();
    if (trimmed.startsWith('<')) {
      return prettyPrintXml(str);
    }
    return str;
  }
}

const IntegrationNodeTestTab: React.FC<Props> = ({ nodeId, integrationId, parameters, inheritAuth, authBasic, authApiToken, authOAuth2, unsaved }) => {
  const [activeSubTab, setActiveSubTab] = useState<string>('input');
  const [paramValues, setParamValues] = useState<TestParameter[]>([]);
  const [connectionId, setConnectionId] = useState<number | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const needsAuth = inheritAuth || authBasic || authApiToken || authOAuth2;

  useEffect(() => {
    setParamValues((prev) => {
      const testable = parameters.filter((p) => TESTABLE_TYPES.includes(p.type));
      return testable.map((p) => {
        const existing = prev.find((v) => v.name === p.name);
        return {
          name: p.name,
          type: p.type,
          value: existing?.value ?? String(p.default_value ?? ''),
        };
      });
    });
  }, [parameters]);

  useEffect(() => {
    if (!needsAuth) return;
    const fetchConnections = async () => {
      setConnectionsLoading(true);
      try {
        const response = await api.get('/connections', {
          params: { limit: 2000 },
        });
        setConnections(response.data.connections ?? []);
      } catch (err) {
        console.error('Failed to fetch connections:', err);
      } finally {
        setConnectionsLoading(false);
      }
    };
    fetchConnections();
  }, [needsAuth]);

  const handleParamChange = (name: string, value: string) => {
    setParamValues((prev) => prev.map((p) => (p.name === name ? { ...p, value } : p)));
  };

  const handleTest = async () => {
    setTesting(true);
    setTestError(null);
    setResult(null);

    const parameter_values: Record<string, string> = {};
    for (const p of paramValues) {
      parameter_values[p.name] = p.value;
    }

    try {
      const response = await api.post(`/integrations/${integrationId}/nodes/${nodeId}/test`, {
        parameter_values,
        ...(connectionId !== null ? { connection_id: connectionId } : {}),
      });
      setResult(response.data);
    } catch (err: any) {
      setTestError(err.response?.data?.error || err.message || 'Test request failed');
    } finally {
      setTesting(false);
    }
  };

  const isSuccess = result !== null && result.success && result.http_status !== null && result.http_status < 400;
  const selectedConnectionOption = connectionId ? (connections.find((c) => c.connection_id === connectionId) ?? null) : null;

  return (
    <div>
      {result && (
        <div className="mb-3">
          {result.success ? (
            <Badge bg={isSuccess ? 'success' : 'danger'} style={{ fontSize: '0.9rem', padding: '0.4em 0.8em' }}>
              {isSuccess ? 'Request Succeeded' : 'Request Failed'}: {result.http_status}
            </Badge>
          ) : (
            <Badge bg="danger" style={{ fontSize: '0.9rem', padding: '0.4em 0.8em' }}>
              Connection Failed
            </Badge>
          )}
        </div>
      )}

      {testError && (
        <Alert variant="danger" className="mb-3">
          {testError}
        </Alert>
      )}

      <Tabs activeKey={activeSubTab} onSelect={(k) => setActiveSubTab(k || 'input')} className="mb-3">
        <Tab eventKey="input" title="Input">
          <Form>
            {needsAuth && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Connection <small className="text-muted">(provides authentication)</small>
                </Form.Label>
                <Select
                  placeholder={connectionsLoading ? 'Loading...' : 'Select a connection...'}
                  isClearable
                  isLoading={connectionsLoading}
                  isDisabled={connectionsLoading || testing}
                  options={connections.map((c) => ({
                    value: c.connection_id,
                    label: c.connection_name,
                  }))}
                  value={
                    selectedConnectionOption
                      ? {
                          value: selectedConnectionOption.connection_id,
                          label: selectedConnectionOption.connection_name,
                        }
                      : null
                  }
                  onChange={(opt) => setConnectionId(opt ? (opt.value as number) : null)}
                />
              </Form.Group>
            )}

            {paramValues.map((param) => (
              <Form.Group key={param.name} className="mb-3">
                <Form.Label>
                  {param.name} <small className="text-muted">({param.type})</small>
                </Form.Label>
                <Form.Control
                  type={param.type === 'Number' ? 'number' : 'text'}
                  value={param.value}
                  onChange={(e) => handleParamChange(param.name, e.target.value)}
                  disabled={testing || unsaved}
                />
              </Form.Group>
            ))}

            {paramValues.length === 0 && !needsAuth && !unsaved && <p className="text-muted mb-3">No String or Number parameters are defined on this node.</p>}

            <div className="d-flex align-items-center gap-3">
              <Button variant="primary" onClick={handleTest} disabled={testing || unsaved}>
                {testing ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
                    Testing...
                  </>
                ) : (
                  'Test'
                )}
              </Button>
              {unsaved && <small className="text-danger">Save your changes before testing.</small>}
            </div>
          </Form>
        </Tab>

        <Tab eventKey="response_headers" title="Response Headers" disabled={!result}>
          {result ? (
            <Table bordered size="sm" style={{ fontSize: '0.85rem' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '35%' }}>Name</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.headers || {}).map(([name, value]) => (
                  <tr key={name}>
                    <td>
                      <code>{name}</code>
                    </td>
                    <td style={{ wordBreak: 'break-all' }}>{String(value)}</td>
                  </tr>
                ))}
                {Object.keys(result.headers || {}).length === 0 && (
                  <tr>
                    <td colSpan={2} className="text-muted text-center">
                      No response headers returned.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">Run a test to see response headers.</p>
          )}
        </Tab>

        <Tab eventKey="response_body" title="Response Body" disabled={!result}>
          {result ? (
            <Form.Control
              as="textarea"
              readOnly
              value={result.error ? result.error : formatBody(result.body)}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.83rem',
                minHeight: '320px',
                whiteSpace: 'pre',
                overflowX: 'auto',
                resize: 'vertical',
              }}
            />
          ) : (
            <p className="text-muted">Run a test to see the response body.</p>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default IntegrationNodeTestTab;
